import { type ReactNode, useMemo, useState } from 'react'
import { Dropdown, DropdownGroup, DropdownTextInput } from 'shared/ui/Dropdown'
import { DropdownContent } from 'shared/ui/Dropdown/ui/DropdownContent'
import { Scrollbar } from 'shared/ui/Scrollbar'
import { prepareSearchRegex } from 'shared/lib/prepareSearch'
import { type IAgentActionType } from 'entities/AgentActions'
import { type IAgentActionItem } from '../types/types'

import styles from './styles.module.scss'

type IActionsDropdownProps = {
  handleAddAction: (action: IAgentActionType) => void
  items: IAgentActionItem[]
  triggerComponent: () => ReactNode
  dropdownWidth?: number
  handleToggleDropdown?: () => void
  groupTitle?: string
}

export const ActionsDropdown = ({
  dropdownWidth,
  handleToggleDropdown,
  handleAddAction,
  items,
  groupTitle,
  triggerComponent,
}: IActionsDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState('')

  const termActions = useMemo(() => {
    const term = searchTerm.trim()
    if (!term) return items
    const searchRegex = prepareSearchRegex(term)
    return items.filter((action) => searchRegex.test(action.label))
  }, [searchTerm, items])

  const handleToggle = (open: boolean) => {
    if (open) setSearchTerm('')
    handleToggleDropdown?.()
  }

  return (
    <Dropdown
      ariaLabel='AgentActionsAdd'
      width={dropdownWidth}
      onToggle={handleToggle}
      triggerComponent={triggerComponent}
      placement='bottom'
      customComponent={(close) => (
        <DropdownContent noPadding>
          <DropdownTextInput
            textFieldProps={{
              value: searchTerm,
              onChange: setSearchTerm,
              InputProps: { placeholder: 'Search' },
            }}
          />
          <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={300}>
            {termActions.length > 0 ? (
              <DropdownGroup
                title={groupTitle}
                items={termActions}
                isScroll={false}
                onChange={({ id }) => {
                  close()
                  handleAddAction(id as IAgentActionType)
                }}
                stylesWrap={{ borderBottom: 'none' }}
              />
            ) : (
              <div className={styles.noResults}>No results found</div>
            )}
          </Scrollbar>
        </DropdownContent>
      )}
    />
  )
}
